import { useState } from "react";
import { useSelector } from 'react-redux';
import PrintLink from '../PrintLink/PrintLink';
import HelpModal from '../HelpModal/HelpModal';
import ShareModal from '../ShareModal/ShareModal';
import FeedbackModal from '../FeedbackModal/FeedbackModal';
import { ChamProjectsOpenButton } from '../ChamProjects/ChamProjects';

import styles from './TopMenu.module.css';

// Helper function to convert image URL to base64
async function convertImageToBase64(imageUrl) {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

export default function TopMenu({ variant = 'full' }) {
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [isShareOpen, setIsShareOpen] = useState(false);
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
    const [saveBtnText, setSaveBtnText] = useState('Save');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    
    const isMinimal = variant === 'minimal';
    
    // Shared state
    const menuActiveTab = useSelector(state => state.shared.menuActiveTab);
    const selectedFloorPlanData = useSelector(state => state.shared.selectedFloorPlanData);
    
    // Features state
    const selectedFloorPlanImageUrl = useSelector(state => state.features.selectedFloorPlanImageUrl);
    const selectedSurfaces = useSelector(state => state.features.selectedSurfaces);
    const exteriorAppliedProducts = useSelector(state => state.features.exteriorAppliedProducts);
    const kitchenAppliedProducts = useSelector(state => state.features.kitchenAppliedProducts);
    const selectedPaintPackage = useSelector(state => state.features.selectedPaintPackage);
    const vizDataByTab = useSelector(state => state.features.vizDataByTab);
    const selectedInteriorFloorPlan = useSelector(state => state.features.selectedInteriorFloorPlan);
    const floorPlanInfo = useSelector(state => state.features.floorPlanInfo);

    const handleShareModalButtonClick = () => {
        setIsShareOpen(true);
    };

    const handleFeedbackModalButtonClick = () => {
        setIsFeedbackOpen(true);
    };

    const handleHelpButtonClick = () => {
        setIsHelpOpen(true);
    };

    const handleStartOverClick = () => {
        window.location.href = '/';
    };

    const handleSaveClick = () => {
        setSaveBtnText('Saving...');
        
        // Build project data from Redux state
        const projectData = {
            // Shared state
            menuActiveTab,
            selectedFloorPlanData,
            // Features state
            selectedSurfaces,
            exteriorAppliedProducts,
            kitchenAppliedProducts,
            selectedPaintPackage,
            vizDataByTab,
            selectedInteriorFloorPlan,
            floorPlanInfo
        };
        
        window.IMWC.lizard.getShortener('', "Avencia", JSON.stringify(projectData), '{}').then(function (r) {
            const arr = r.data.shortUrl.split('/');
            const shortcode = arr[arr.length - 1];
            // Use the stored floor plan image URL instead of chamViz.render
            const mainVizSrc = selectedFloorPlanImageUrl;
            
            if (!mainVizSrc) {
                // If no visualizer image, save without thumbnail
                document.querySelector('cham-projects').saveProject('spec', '', '', shortcode);
                setSaveBtnText('Saved!');
                setTimeout(() => {
                    setSaveBtnText('Save');
                }, 1000);
                return;
            }
            
            convertImageToBase64(mainVizSrc).then(function (b64Img) {
                window.IMWC.resizeBase64Image(b64Img, 500).then(function(newBase64){
                    document.querySelector('cham-projects').saveProject('spec', '', newBase64, shortcode);
                    setSaveBtnText('Saved!');
                    setTimeout(() => {
                        setSaveBtnText('Save');
                    }, 1000);
                });
            }).catch((error) => {
                alert('Failed to save.');
                console.error(error);
                setSaveBtnText('Save');
            });
        }).catch((error) => {
            alert('Failed to save.');
            console.error(error);
            setSaveBtnText('Save');
        });
    };


    return (
        <>
            <nav className={styles.topMenu} aria-label="Main Navigation">
                <button 
                    className={styles.hamburger} 
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    aria-label="Toggle menu"
                    aria-expanded={isMobileMenuOpen}
                >
                    <span className={`${styles.hamburgerLine} ${isMobileMenuOpen ? styles.open : ''}`}></span>
                    <span className={`${styles.hamburgerLine} ${isMobileMenuOpen ? styles.open : ''}`}></span>
                    <span className={`${styles.hamburgerLine} ${isMobileMenuOpen ? styles.open : ''}`}></span>
                </button>
                <ul className={isMobileMenuOpen ? styles.menuOpen : ''}>
                    {!isMinimal && (
                        <li>
                            <button type="button" onClick={handleStartOverClick}>
                                Start Over
                            </button>
                        </li>
                    )}
                    {!isMinimal && (
                        <li>
                            <button type="button" onClick={handleSaveClick}>
                                {saveBtnText}
                            </button>
                        </li>
                    )}
                     <li>
                        <ChamProjectsOpenButton title="My Home" className="" aria-label="My Projects Button" />
                    </li>
                    {!isMinimal && (
                        <li>
                            <PrintLink />
                        </li>
                    )}
                    {!isMinimal && (
                        <li>
                            <button type="button" onClick={handleShareModalButtonClick}>
                                Copy Link
                            </button>
                        </li>
                    )}
                    {!isMinimal && (
                        <li>
                            <button type="button" onClick={handleFeedbackModalButtonClick}>
                                Feedback
                            </button>
                        </li>
                    )}
                  <li>
                        <button type="button" onClick={handleHelpButtonClick}>
                            Help
                        </button>
                    </li> 
                </ul>
            </nav>

            <HelpModal 
                isOpen={isHelpOpen}
                onClose={() => setIsHelpOpen(false)}
            />

            <ShareModal 
                isOpen={isShareOpen}
                onClose={() => setIsShareOpen(false)}
            />

            <FeedbackModal 
                isOpen={isFeedbackOpen}
                onClose={() => setIsFeedbackOpen(false)}
            />
        </>
    );
}
